import React from "react";
import "../../../../styles/css/table.scss";
import "../../../../App.css";
import { RouteComponentProps, RouterProps } from "react-router-dom";
import { appContext } from "../../../../AppContext";
import { DateTime } from "luxon";
import { DateHelper } from "../../../../Common/DateHelper";
import { Box, Grid, SelectChangeEvent } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import IbssSvgIcon from "../../../../Components/Icons/SvgIcon/IbssSvgIcon";
import IbssFilter from "../../../../Components/Forms/Filter/IbssFilter";
import IbssFilterHeader from "../../../../Components/Forms/DateRange/IbssFilterHeader";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { QueryParams } from "./QueryParams";
import { DateRangeHelper } from "../../../../Common/DateRangeHelper";
import { Constants } from "../../../../Common/Constants";
import { BookingDetails, TicketedEvent } from "./DataModels";
import CreateIcon from '@mui/icons-material/Create';
import { PagedResponse } from "../../../../Providers.Api/Models";
import ServerDataGrid, { DataGridQueryResult, IDataQuery, IPages } from "../../../../Components/Data/ServerDataGrid/ServerDataGrid";
import { rights } from "../../../../Common/RightsManager";
import Helper from "../../../../Common/Helper";

class ListEvents extends IbssPage<IProps, IState, QueryParams>
{
    private get appState() { return appContext().state; }
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private dataGridRef = React.createRef<ServerDataGrid<IEvents>>();
    private get dataGrid() { return this.dataGridRef.current; }
    private pageCacheKey = '418a435a-3lda-41n7-bb1a-1y9876ba4c23';
    private userCanUpdate: boolean;


    constructor(props: IProps) 
    {
        super(props, new QueryParams());
        this.userCanUpdate = rights.DATAMODEL.TicketedEvents.Update.has();
        
        this.state =
        {
            isLoading: false,
            buildingId: this.appState.buildingId,
            searchTerm: "",
            daysFilter: this.labels.HubLabelThisWeek,
            startDate: DateHelper.now(),
            endDate: DateHelper.now(),
            dateDisabled: true,
            events: [],
            pages: {
                pages: [],
                pageIndex: 0,
                skipToken: '',
            },
        };
    }

    public async queryParamsDidUpdate(firstLoad: boolean, prevParams: QueryParams): Promise<void>
    {
        if (firstLoad)
        {
            this.clearCache();
        }

        const queryParams = this.queryParams;
        if (!queryParams.building)
        {
            return this.pushQueryParams({ building: this.appState.buildingId }, true);
        }


        const buildingId = queryParams.building;
        const buildingName = this.local.getNodeData().Regions.flatMap(i => i.Buildings).find(i => i.Node_Id == buildingId)?.Name;
        this.pageTitle = (buildingName ? this.labels.funcEvents_S +  " - " + buildingName : this.labels.HubLabelFacilityManagement);

        const dateRange = new DateRangeHelper(buildingId)
            .addToday()
            .addTomorrow()
            .addThisWeek()
            .addNextWeek()
            .addThisMonth()
            .addNextMonth()
            .getDateRange(queryParams.period, this.labels.HubLabelThisWeek, queryParams.start, queryParams.end);

        if (queryParams.period != dateRange.period)
        {
            return this.pushQueryParams({ period: dateRange.period }, true);
        }

        await this.setStateAsync({
            buildingId: buildingId,
            daysFilter: dateRange.period,
            dateDisabled: (dateRange.period != this.labels.HubLabelCustom),
            startDate: dateRange.start,
            endDate: dateRange.end,
            searchTerm: queryParams.filter ?? '',
        });
        await this.dataGrid?.refresh();
    }

    private clearCache(): void
    {
        appContext().inMemoryCache.clear(i => i.startsWith(this.pageCacheKey + '_'));
    }    
    
    private async dataQueryChanged(gridQuery: IDataQuery): Promise<DataGridQueryResult<IEvents>> 
    {
        this.setState({ isLoading: true });

        try {
            const { startDate, endDate, buildingId } = this.state;
            const start = startDate.date().toUtcByNode(buildingId).toISO();
            const end = endDate.plus({ days: 1 }).date().toUtcByNode(buildingId).toISO();
        
            const filter = `Event_Start_DateTime ge datetime'${start}' and Event_Start_DateTime lt datetime'${end}'`;
        
            const filterEndpoint = {
                name: 'ibssApiClientV2.v2.byNodeid.ticketedevents.get',
                options: {
                    nodeId: buildingId,
                    filter,
                    recursive: true,
                    select: TicketedEvent,
                    skipToken: gridQuery.skipToken,
                    top: 200
                }
            };

            const response = await appContext().inMemoryCache.lazyGetWithQuickExpiry(
                `${this.pageCacheKey}_${JSON.stringify(filterEndpoint)}`,
                () => appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.get<PagedResponse<TicketedEvent[]>>(filterEndpoint.options)
            );
        
            if (!response.value.length) 
            {
                this.setState({ events: [], isLoading: false });
                return new DataGridQueryResult([], response.skipToken);
            }
        
            const filterEventBookingsIds = response.value.map(event => `Booking_Id eq '${event.Booking_Id}'`).join(' or ');
            let bookingsApiResponse: PagedResponse<BookingDetails[]> = { skipToken: '', value: [] };

            if (filterEventBookingsIds.length > 0)
            {
                try 
                {
                    bookingsApiResponse = await appContext().ibssApiClientV2.v2.byNodeid.bookings.get<PagedResponse<BookingDetails[]>>({
                        nodeId: buildingId,
                        select: BookingDetails,
                        suppressErrorPopup: true,
                        filter: filterEventBookingsIds,
                        skipToken: '',
                    });
                } 
                catch (error) 
                {
                }
            }
        
            const bookingMap = new Map(
                bookingsApiResponse.value.map(booking => [booking.Booking_Id, booking])
            );
        
            const rows = response.value.map(event => {
                const booking = bookingMap.get(event.Booking_Id);
                return {
                    bookingId: event.Booking_Id,
                    eventTitle: event.Event_Title,
                    eventDate: event.Event_Date,
                    eventStart: event.Event_Start_DateTime,
                    eventEnd: event.Event_End_DateTime,
                    ticketedEventId: event.Ticketed_Event_Id ?? '',
                    nodeId: event.Node_Id,
                    spaceName: booking?.Space_Name ?? '-',
                    spaceId: booking?.Space_Id ?? '-',
                };
            });
        
            this.setState({ events: rows, isLoading: false });
            return new DataGridQueryResult(rows, response.skipToken);
        } catch (error) 
        {
            this.setState({ events: [], isLoading: false });
            return new DataGridQueryResult([], '');
        }
    }
    

    private async dateRangeChanged(e: SelectChangeEvent<string>): Promise<void>
    {
        const periodIsCustom = (e.target.value == this.labels.HubLabelCustom);
        if(periodIsCustom)
        {
            this.pushQueryParams({ period: e.target.value , start: this.state.startDate , end: this.state.endDate })
        }
        else
        {
            this.pushQueryParams({ period: e.target.value , start: undefined , end: undefined });
        }
    }

    private filterNotifications(searchTerm: string): IPages<IEvents>
    {
        const rows = this.state.pages.pages.map(x => x.rows).flat();

        const filteredNotifications = rows.filter(notification =>
        {
            let key: keyof IEvents;
            for (key in notification)
            {
                if (notification[key]?.toString().toLowerCase().includes(searchTerm.toLocaleLowerCase()))
                {
                    return true;
                }
            }
            return false;
        });
        return { pages: [{ rows: filteredNotifications }], skipToken: '', pageIndex: 0 };
    }

    private debounceFilterTextTimer: NodeJS.Timeout | null = null;
    private searchTermChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void
    {
        this.setState({ searchTerm: event.target.value });
        clearTimeout(this.debounceFilterTextTimer ?? undefined);

        this.debounceFilterTextTimer = setTimeout(
            () => this.pushQueryParams({ filter: event.target.value === '' ? undefined : event.target.value }),
            Constants.longDebounceTimeInMilliseconds,
        );
    }

    private edit(event: IEvents): React.ReactNode
    {
        const { history } = this.props;
        return (
            this.userCanUpdate ?
                <IbssSvgIcon onClick={() => history.push(`/oneLens/event/${event.nodeId}/${event.bookingId}/${event.spaceId}/${event.ticketedEventId}`)}>
                    <CreateIcon />
                </IbssSvgIcon>
                : <p>-</p>
        )
    }
    private async startDateChanged(e: DateTime): Promise<void>
    {
        if (!e.isValid)
        {
            return;
        }
        this.pushQueryParams({ start: e })
    }

    private async endDateChanged(e: DateTime): Promise<void>
    {
        if (!e.isValid)
        {
            return;
        }
        this.pushQueryParams({ end: e  })
    }

    public render(): JSX.Element 
    {
        const columns: GridColDef<IEvents>[] =
            [
                {
                    field: Helper.nameOf<IEvents>('eventTitle'),
                    headerName: this.labels.funcEventName_S,
                    flex: 1
                },
                {
                    field: Helper.nameOf<IEvents>('eventDate'),
                    headerName: this.labels.HubLabelDate,
                    flex: 1,
                    renderCell: (params) => params.row.eventDate.toLocaleDateString()
                },
                {
                    field: Helper.nameOf<IEvents>('eventStart'),
                    headerName: this.labels.HubLabelStart,
                    flex: 1,
                    renderCell: (params) => params.row.eventStart.toLocaleTimeString()
                },
                {
                    field: Helper.nameOf<IEvents>('eventEnd'),
                    headerName: this.labels.HubLabelEnd,
                    flex: 1,
                    renderCell: (params) => params.row.eventEnd.toLocaleTimeString()
                },
                {
                    field: Helper.nameOf<IEvents>('spaceName'),
                    headerName: this.labels.HubLabelSpace,
                    flex: 1
                },
                {
                    field: "",
                    headerName: this.labels.HubLabelAction,
                    minWidth: 120,
                    flex: 1,
                    filterable: false,
                    sortable: false,
                    renderCell: (params) => <>{this.edit(params.row)}</>
                }
            ];

        const filteredNotifications = this.filterNotifications(this.state.searchTerm);

        return (
            <>
                <Box className="page-height-exct-header">
                    <div className="rightPanel-main-content">
                        <div className="table-panel">
                            <IbssFilterHeader
                                title={this.labels.funcManageEvents_S}
                                selectedPreset={this.state.daysFilter}
                                presetChanged={(e) => this.dateRangeChanged(e)}
                                startDate={this.state.startDate.toJSDate()}
                                endDate={this.state.endDate.toJSDate()}
                                datesDisabled={this.state.dateDisabled}
                                onStartDateChange={i => this.startDateChanged(i)}
                                onEndDateChange={i => this.endDateChanged(i)}
                                presets={[this.labels.HubLabelTomorrow, this.labels.HubLabelThisWeek, this.labels.HubLabelNextWeek, this.labels.HubLabelThisMonth, this.labels.HubLabelNextMonth]}
                            />
                            <Grid
                                container
                                rowSpacing={1}
                                sx={{ display: "flex", alignItems: "center", mt: 0, ml: 0 }}
                            >
                                <Grid item md={12}>
                                    <Box>
                                        <IbssFilter
                                            showExport={false}
                                            searchTerm={this.state.searchTerm}
                                            onSearchTermChange={(event) => this.searchTermChange(event)}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            <div className="position-relative">
                                <Box sx={{ mt: 1 }}>
                                    <ServerDataGrid
                                        loading={this.state.isLoading}
                                        getRowId={(row: IEvents) => row.ticketedEventId ?? ''}
                                        className="view-events-data-grid"
                                        loadDataOnMount={false}
                                        ref={this.dataGridRef}
                                        onDataQueryChange={i => this.dataQueryChanged(i)}
                                        columns={columns}
                                        disableRowSelectionOnClick
                                        pageSizeOptions={[100]}
                                        pages={this.state.searchTerm.length > 0 ? filteredNotifications : this.state.pages}
                                        onPagesChange={pages => this.setStateAsync({ pages: pages })}
                                    />
                                </Box>
                            </div>
                        </div>
                    </div>
                </Box>
            </>
        );
    }
}
interface IProps extends RouteComponentProps
{
}

interface IState 
{
    isLoading: boolean;
    buildingId: number;
    searchTerm: string;
    daysFilter: string;
    startDate: DateTime;
    endDate: DateTime;
    dateDisabled: boolean;
    pages: IPages<IEvents>;
    events: IEvents[];
}
interface IEvents 
{
    nodeId: number;
    eventTitle: string;
    ticketedEventId: string | null;
    eventDate: DateTime;
    eventStart: DateTime;
    eventEnd: DateTime;
    bookingId: string;
    spaceName: string;
    spaceId: string;
}

export default ListEvents;
